import React, { FC, useEffect, useRef, useState, useCallback } from "react"
import * as pdfjsLib from "pdfjs-dist"
import { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api"
import {
	ZoomIn,
	ZoomOut,
	ChevronLeft,
	ChevronRight,
	Printer,
	Loader,
} from "lucide-react"
import { useURLBlobFromUrl } from "@/hooks/useURLBlobFromUrl"
// Configure the worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

interface PDFViewerProps {
	url: string
	pageNumber?: number
}

const RES_SCALE = 4

/**
 * A component for displaying PDF documents using PDF.js
 */
export const PDFViewer: FC<PDFViewerProps> = ({ url, pageNumber = 1 }) => {
	const { data: blobUrl, isLoading: isBlobLoading } = useURLBlobFromUrl(url)

	const canvasRef = useRef<HTMLCanvasElement>(null)
	const [numPages, setNumPages] = useState<number>(0)
	const [currentPage, setCurrentPage] = useState<number>(pageNumber)
	const [loading, setLoading] = useState<boolean>(true)
	const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null)
	const [scale, setScale] = useState<number>(1) // Default scale reduced to show more content

	// Load the PDF document when the component mounts or the URL changes
	useEffect(() => {
		setLoading(true)

		const loadPdf = async () => {
			try {
				if (!blobUrl) {
					return
				}

				// Load the PDF document
				const loadingTask = pdfjsLib.getDocument(blobUrl)
				const pdf = await loadingTask.promise

				// Store the document and update state
				setPdfDocument(pdf)
				setNumPages(pdf.numPages)
				setCurrentPage(1) // Reset to page 1 when a new document is loaded
				setLoading(false)
			} catch (error) {
				console.error("Error loading PDF:", error)
				setLoading(false)
			}
		}

		loadPdf()

		// Cleanup function
		return () => {
			if (pdfDocument) {
				pdfDocument.destroy().catch(console.error)
			}
		}
	}, [blobUrl])

	// Render the current page whenever the page number changes or the document loads
	useEffect(() => {
		if (!pdfDocument || !canvasRef.current) return

		const renderPage = async () => {
			try {
				// Get the page
				const page = await pdfDocument.getPage(currentPage)

				// Set up canvas and rendering context
				const canvas = canvasRef.current
				const context = canvas.getContext("2d")

				if (!context) {
					throw new Error("Canvas context could not be created")
				}

				// Get viewport for rendering (high-res)
				const renderViewport = page.getViewport({ scale: scale * RES_SCALE })
				canvas.width = renderViewport.width
				canvas.height = renderViewport.height

				// Get viewport for display size (target-res)
				const displayViewport = page.getViewport({ scale: scale })
				canvas.style.width = displayViewport.width + "px"
				canvas.style.height = displayViewport.height + "px"

				// Render the page using the high-res viewport
				const renderContext = {
					canvasContext: context,
					viewport: renderViewport, // Use renderViewport here
				}

				await page.render(renderContext).promise
			} catch (error) {
				console.error("Error rendering PDF page:", error)
			}
		}

		renderPage()
	}, [pdfDocument, currentPage, scale])

	// Handle page navigation
	const goToPreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1)
		}
	}

	const goToNextPage = () => {
		if (currentPage < numPages) {
			setCurrentPage(currentPage + 1)
		}
	}

	// Zoom controls
	const zoomIn = () => {
		setScale((prevScale) => Math.min(prevScale + 0.25, 5.0)) // Max zoom of 5.0 (500%)
	}

	const zoomOut = () => {
		setScale((prevScale) => Math.max(prevScale - 0.25, 0.25)) // Min zoom of 0.25 (25%)
	}

	const resetZoom = () => {
		setScale(1) // Reset to default zoom
	}

	// Print the PDF using a hidden iframe so that only the document is printed
	const handlePrint = useCallback(() => {
		// Create an off-screen iframe pointing to the PDF URL
		const iframe = document.createElement("iframe")

		// Keep the iframe hidden but mounted so the print dialog remains open
		iframe.style.position = "fixed"
		iframe.style.right = "0"
		iframe.style.bottom = "0"
		iframe.style.width = "0"
		iframe.style.height = "0"
		iframe.style.border = "0"

		iframe.src = blobUrl

		const cleanup = () => {
			iframe.remove()
			window.removeEventListener("afterprint", cleanup)
		}

		// Attach afterprint listener to clean up once the user is done
		window.addEventListener("afterprint", cleanup)

		iframe.onload = () => {
			try {
				iframe.contentWindow?.focus()
				iframe.contentWindow?.print()

				// Fallback in case afterprint doesn't fire (some browsers)
				setTimeout(cleanup, 10000)
			} catch (error) {
				console.error("Error printing PDF:", error)
				cleanup()
			}
		}

		document.body.appendChild(iframe)
	}, [blobUrl])

	// Listen for Cmd+P / Ctrl+P to trigger PDF print instead of default browser behaviour
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			const isPrintShortcut =
				(e.key === "p" || e.key === "P") && (e.metaKey || e.ctrlKey)
			if (isPrintShortcut) {
				e.preventDefault()
				handlePrint()
			}
		}

		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [handlePrint])

	return (
		<div className="flex flex-col items-center w-full relative h-screen">
			{loading ? (
				<div className="flex flex-col items-center justify-center p-8">
					<Loader size={24} className="animate-spin mb-2" />
					<p>Loading PDF...</p>
				</div>
			) : (
				<>
					<div className="sticky top-0 z-10 flex justify-center items-center py-2 space-x-2 w-full bg-white border-b relative pr-4">
						<button
							onClick={zoomOut}
							className="p-1 border rounded"
							title="Zoom Out"
						>
							<ZoomOut size={16} />
						</button>
						<button
							onClick={resetZoom}
							className="px-2 py-1 border rounded text-xs"
							title="Reset Zoom"
						>
							{Math.round(scale * 100)}%
						</button>
						<button
							onClick={zoomIn}
							className="p-1 border rounded"
							title="Zoom In"
						>
							<ZoomIn size={16} />
						</button>

						{numPages > 1 && (
							<>
								<div className="h-4 mx-1 border-l"></div>
								<button
									onClick={goToPreviousPage}
									disabled={currentPage <= 1}
									className="p-1 border rounded disabled:opacity-50"
									title="Previous Page"
								>
									<ChevronLeft size={16} />
								</button>
								<span className="text-xs flex items-center">
									{currentPage} / {numPages}
								</span>
								<button
									onClick={goToNextPage}
									disabled={currentPage >= numPages}
									className="p-1 border rounded disabled:opacity-50"
									title="Next Page"
								>
									<ChevronRight size={16} />
								</button>
							</>
						)}

						{/* Print Button – positioned on the far right */}
						<button
							onClick={handlePrint}
							className="p-1 border rounded absolute right-2"
							title="Print PDF"
						>
							<Printer size={16} />
						</button>
					</div>

					<div className="flex-grow overflow-auto w-full">
						<div className="border rounded-md shadow-sm w-full mt-1">
							<div className="flex items-center">
								<canvas ref={canvasRef} className="block mx-auto"></canvas>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	)
}
